import { useMemo, useState, type ReactNode } from 'react';
import { DASH } from '../lib/format';

export interface Column<R> {
  key: string;
  header: string;
  cell: (row: R) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: R) => string | number | null;
  numeric?: boolean;
}

export interface DataTableProps<R> {
  caption: string;
  rows: R[];
  columns: Column<R>[];
  rowKey: (row: R) => string;
  empty?: ReactNode;
  /** Collapse rows into labelled cards under 640px (default on). */
  responsive?: boolean;
}

type Sort = { key: string; dir: 'ascending' | 'descending' } | null;

function compare(a: string | number | null, b: string | number | null) {
  if (a === b) return 0;
  if (a === null) return 1; // nulls last in both directions
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function DataTable<R>({
  caption,
  rows,
  columns,
  rowKey,
  empty,
  responsive = true,
}: DataTableProps<R>) {
  const [sort, setSort] = useState<Sort>(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const out = [...rows].sort((x, y) => compare(col.sortValue!(x), col.sortValue!(y)));
    if (sort.dir === 'descending') {
      const nonNull = out.filter((r) => col.sortValue!(r) !== null).reverse();
      return [...nonNull, ...out.filter((r) => col.sortValue!(r) === null)];
    }
    return out;
  }, [rows, columns, sort]);

  if (!rows.length && empty) return <>{empty}</>;

  const toggle = (key: string) =>
    setSort((s) =>
      s?.key === key && s.dir === 'ascending'
        ? { key, dir: 'descending' }
        : { key, dir: 'ascending' },
    );

  return (
    <div className="w-full overflow-x-auto">
      <table className="fl-table" data-responsive={responsive || undefined}>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? sort!.dir : c.sortable ? 'none' : undefined}
                  data-numeric={c.numeric || undefined}
                >
                  {c.sortable ? (
                    <button type="button" className="fl-sort" onClick={() => toggle(c.key)}>
                      {c.header}
                      <span aria-hidden="true">
                        {active ? (sort!.dir === 'ascending' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={rowKey(r)}>
              {columns.map((c) => {
                const v = c.cell(r);
                return (
                  <td key={c.key} data-label={c.header} data-numeric={c.numeric || undefined}>
                    {v === null || v === undefined ? DASH : v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
